import { IFavoriteOriginWithMonths } from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";
import {
  DragState,
  ProductSectionOrder,
  SectionKey,
} from "../../../types/IOriginManager";

// Derives the unique key for a row based on its origin type and location ID.
export const getRowKey = (origin: IFavoriteOriginWithMonths) =>
  `${origin.competitor ? "competitor" : "koch"}-${origin.location.id}`;

// How far a scroll container must move so `row` ends up vertically centered within it.
export const getCenteringScrollOffset = (
  container: { top: number; height: number },
  row: { top: number; height: number },
) => row.top - container.top - container.height / 2 + row.height / 2;

// Combines server-side and local-only origins into the set the user currently intends to have.
export const getActiveOrigins = (
  savedOrigins: IFavoriteOriginWithMonths[],
  newlyAddedOrigins: IFavoriteOriginWithMonths[],
  deletedKeys: Set<string>,
): IFavoriteOriginWithMonths[] => {
  const map = new Map<string, IFavoriteOriginWithMonths>();

  savedOrigins.forEach((origin) => {
    const key = getRowKey(origin);
    if (!deletedKeys.has(key)) {
      map.set(key, origin);
    }
  });

  newlyAddedOrigins.forEach((origin) => {
    const key = getRowKey(origin);
    if (!deletedKeys.has(key)) {
      map.set(key, origin);
    }
  });

  return Array.from(map.values());
};

const DEFAULT_SELECTED_MONTH_COUNT = 3;

export const getDefaultSelectedMonths = (startMonth: number) =>
  Array.from(
    { length: DEFAULT_SELECTED_MONTH_COUNT },
    (_, i) => MONTHS_LOCAL[(startMonth + i) % 12],
  );

export const sortMonthsByTimeline = (
  months: string[],
  currentMonth: number,
) => {
  const uniqueMonths = Array.from(new Set(months));
  return uniqueMonths.sort(
    (a, b) =>
      ((MONTHS_LOCAL.indexOf(a) - currentMonth + 12) % 12) -
      ((MONTHS_LOCAL.indexOf(b) - currentMonth + 12) % 12),
  );
};

export const getDefaultProductSectionOrder = (): ProductSectionOrder => ({
  koch: [],
  competitor: [],
});

export const mergeSectionOrder = (
  existing: string[] | undefined,
  currentKeys: string[],
) => {
  const kept = (existing ?? []).filter((key) => currentKeys.includes(key));
  const newKeys = currentKeys.filter((key) => !kept.includes(key));
  return [...kept, ...newKeys];
};

export const sortRowsBySectionOrder = (
  sectionRows: IFavoriteOriginWithMonths[],
  orderedKeys: string[],
) => {
  if (orderedKeys.length === 0) {
    return sectionRows;
  }

  const orderIndex = new Map<string, number>();
  orderedKeys.forEach((key, index) => {
    orderIndex.set(key, index);
  });

  return [...sectionRows].sort((left, right) => {
    const leftKey = getRowKey(left);
    const rightKey = getRowKey(right);
    const leftIndex = orderIndex.get(leftKey);
    const rightIndex = orderIndex.get(rightKey);

    if (leftIndex === undefined && rightIndex === undefined) {
      return 0;
    }
    if (leftIndex === undefined) {
      return 1;
    }
    if (rightIndex === undefined) {
      return -1;
    }
    return leftIndex - rightIndex;
  });
};

export const isValidDragTarget = (
  dragState: DragState | null,
  rowKey: string,
  section: SectionKey,
) =>
  Boolean(
    dragState && dragState.section === section && dragState.rowKey !== rowKey,
  );

export const reorderSectionRowKeys = (
  currentOrder: string[],
  sourceRowKey: string,
  targetRowKey: string,
  placeAfter: boolean,
): string[] | null => {
  const sourceIndex = currentOrder.indexOf(sourceRowKey);
  const targetIndex = currentOrder.indexOf(targetRowKey);

  if (sourceIndex < 0 || targetIndex < 0) {
    return null;
  }

  const nextOrder = [...currentOrder];
  const [moved] = nextOrder.splice(sourceIndex, 1);
  let insertIndex = targetIndex;

  if (sourceIndex < targetIndex) {
    insertIndex -= 1;
  }
  if (placeAfter) {
    insertIndex += 1;
  }

  nextOrder.splice(insertIndex, 0, moved);
  return nextOrder;
};

export const updateProductSectionOrder = (
  prevOrder: ProductSectionOrder,
  section: SectionKey,
  nextOrder: string[],
): ProductSectionOrder => ({
  koch: section === "koch" ? nextOrder : prevOrder.koch,
  competitor: section === "competitor" ? nextOrder : prevOrder.competitor,
});

export const sortFavoriteOriginsByOrder = (
  origins: IFavoriteOriginWithMonths[],
) =>
  origins
    .map((origin, index) => ({ origin, index }))
    .sort((left, right) => {
      const leftOrder = Number.isFinite(left.origin.order)
        ? Number(left.origin.order)
        : Infinity;
      const rightOrder = Number.isFinite(right.origin.order)
        ? Number(right.origin.order)
        : Infinity;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return left.index - right.index;
    })
    .map((item) => item.origin);

export const applySectionOrderNumbers = (
  favorites: IFavoriteOriginWithMonths[],
  productOrder: ProductSectionOrder,
  sectionToRecompute?: SectionKey,
) => {
  const kochOrderById = new Map<string, number>();
  const competitorOrderById = new Map<string, number>();
  productOrder.koch.forEach((rowKey, index) => {
    kochOrderById.set(rowKey.replace(/^koch-/, ""), index + 1);
  });
  productOrder.competitor.forEach((rowKey, index) => {
    competitorOrderById.set(rowKey.replace(/^competitor-/, ""), index + 1);
  });

  return favorites.map((favorite) => {
    const isCompetitor = favorite.competitor;
    const shouldRecompute =
      sectionToRecompute === undefined ||
      (sectionToRecompute === "competitor" && isCompetitor) ||
      (sectionToRecompute === "koch" && !isCompetitor);

    if (!shouldRecompute) {
      return favorite;
    }

    const nextOrder = isCompetitor
      ? competitorOrderById.get(favorite.location.id)
      : kochOrderById.get(favorite.location.id);

    if (nextOrder === undefined) {
      return favorite;
    }

    return {
      ...favorite,
      order: nextOrder,
    };
  });
};

export const getMarketerIdentifierFromProfile = (
  profile: Record<string, unknown> | undefined,
) =>
  `${String(profile?.given_name ?? "")} ${String(
    profile?.family_name ?? "",
  )}`.trim();

export const formatRequestedAt = (
  requestedAt: string | null | undefined,
): string => {
  if (!requestedAt) return "";
  try {
    const date = new Date(requestedAt);
    const datePart = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} at ${timePart}`;
  } catch {
    return requestedAt;
  }
};
